// React & Redux (Ajout de dayjs pour les dates comme dans Projet)
import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";

// Material UI
import { 
  Button, Grid, IconButton, Typography, Box, Card, 
  Divider, Stack, Tooltip 
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

// Config & Redux Actions
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions/index";
import { colorDarkOrange } from "../../config/color";

// Components & Fetchs
import HostingCostSummary from "./HostingCostSummary";
import AddUpdateHosting from "./AddUpdateHosting";
import addUpdateHostingData from "../../tools/addUpdateHosting";
import AlerteDialog from "../AlerteDialog/AlerteDialog";
import { getAll } from "../../fetchs/get";
import { fetchPutProtect } from "../../fetchs/put";
import { IHosting, PriceData } from "../../interface/hosting";

interface IFormValues {
  name: string;
  description: string;
  client: string;
  environment: string;
  margin: number;
  retentionTime: number;
  storage: number;
  servers: [string];
  nbIP: number;
}

const HostingDetails = ({
  hosting,
  isModified,
  setIsModified
}: {
  hosting: IHosting;
  isModified: boolean;
  setIsModified: Function;
}) => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);

  const [isSwitched, setIsSwitched] = useState<boolean>(true);
  const [isCompared, setIsCompared] = useState<boolean>(false);
  const [isRotated, setIsRotated] = useState(false);
  const [prices, setPrices] = useState<PriceData[]>([]);
  const [openConfirmClose, setOpenConfirmClose] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const getPrices = useCallback(() => {
    if (token) {
      getAll(token, "/hosting/ovh", setPrices, () => dispatch(sessionEnd()), setError);
    }
  }, [token, dispatch]);

  useEffect(() => { getPrices(); }, [getPrices]);

  const updateHosting = async (toUpdate: string, values?: IFormValues) => {
    const body = (toUpdate === "all" && values) ? await addUpdateHostingData(values, prices, hosting) : JSON.stringify({ _id: hosting._id, prices: prices[0] });
    const url = toUpdate === "all" ? "/hosting/update" : "/hosting/updatePrices";
    
    if (token) {
      await fetchPutProtect(token, url, body)
        .then((response) => {
          if (response.status === 200) {
            toUpdate === "all" ? setIsModified(false) : setIsCompared(false);
            dispatch(showSnackbar("Mise à jour réussie", "success"));
            window.location.reload();
          } else if (response.status === 401) {
            dispatch(sessionEnd());
          }
        }).catch((err) => setError(err));
    }
  }

  const togglePanel = () => {
    setIsSwitched(!isSwitched);
    setIsRotated(!isRotated);
  };

  const classicTypes = ["simple", "double"];

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={3} position="relative">
        
        {/* Flèche de bascule identique à Projet */}
        {!isModified && (
          <Tooltip title={isSwitched ? "Masquer détails" : "Afficher détails"}>
            <IconButton 
              onClick={togglePanel}
              sx={{
                position: 'absolute',
                top: 10,
                right: isSwitched ? "26%" : "20px",
                zIndex: 10,
                transition: 'all 0.4s ease',
                transform: isRotated ? 'rotate(90deg)' : 'rotate(270deg)',
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#fff7ed' }
              }}
            >
              <KeyboardArrowDown sx={{ color: colorDarkOrange }} />
            </IconButton>
          </Tooltip>
        )}

        {/* SECTION GAUCHE : TABLEAUX DE COÛTS */}
        <Grid item xs={12} md={isSwitched ? (isModified ? 0 : 9) : 12}>
          {!isModified && (
            <Grid container spacing={2}>
              {hosting.type === "classic" ? (
                classicTypes.map((type) => (
                  <Grid item xs={isSwitched ? 6 : 6} key={type}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="overline" fontWeight={800} color="primary">Formule {type}</Typography>
                      <Typography variant="caption" display="block" color="text.secondary">720 heures / mois</Typography>
                    </Box>
                    <Box sx={{ height: "70vh", overflowY: "auto", pr: 1 }}>
                      <HostingCostSummary hosting={hosting} type={type} prices={prices} isCompared={isCompared} />
                    </Box>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                   <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="overline" fontWeight={800} color="primary">Formule Personnalisée</Typography>
                      <Typography variant="caption" display="block" color="text.secondary">720 heures / mois</Typography>
                    </Box>
                  <HostingCostSummary hosting={hosting} type="personnalisée" prices={prices} isCompared={isCompared} />
                </Grid>
              )}
            </Grid>
          )}
        </Grid>

        {/* SECTION DROITE : CARD DE MÉTHADONNÉES (Identique à Projet) */}
        {isSwitched && (
          <Grid item xs={12} md={isModified ? 12 : 3}>
            <Card
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "18px",
                border: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
                height: "100%",
                minWidth: isModified ? "500px" : "auto",
                mx: isModified ? "auto" : 0
              }}
            >
              {isModified ? (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight={700}>Édition Hébergement</Typography>
                    <IconButton onClick={() => setOpenConfirmClose(true)} size="small"><CloseIcon /></IconButton>
                  </Stack>
                  <AddUpdateHosting 
                    hosting={hosting} 
                    handleSubmit={updateHosting} 
                    edit={true} 
                    isSwitched={hosting.backup.retentionTime !== 0}
                    typeChecked={hosting.type !== "classic"}
                    setTypeChecked={() => {}} 
                  />
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" fontWeight={700}>Hébergement</Typography>
                    <Typography variant="h6" color="primary.main" fontWeight={800}>{hosting.name}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="overline" color="text.secondary" fontWeight={700}>Description</Typography>
                    <Typography variant="body2">{hosting.description || "Aucune description"}</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="overline" color="text.secondary" fontWeight={700}>Client & Env</Typography>
                    </Stack>
                    <Typography variant="subtitle2" fontWeight={700}>{hosting.client.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{hosting.environment} • Marge: {hosting.margin}%</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <PersonOutlineIcon fontSize="small" color="action" />
                      <Typography variant="overline" color="text.secondary" fontWeight={700}>Historique</Typography>
                    </Stack>
                    <Typography variant="caption" display="block"><b>Créé par :</b> {hosting.author}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary">le {dayjs(hosting.createdAt).format("DD/MM/YYYY")}</Typography>
                  </Box>

                  <Stack spacing={1} pt={2}>
                    <Button
                      fullWidth
                      startIcon={<EditTwoToneIcon />}
                      variant="contained"
                      onClick={() => setIsModified(true)}
                      sx={{ borderRadius: "10px", py: 1, boxShadow: 'none' }}
                    >
                      Métadonnées
                    </Button>
                    <Button
                      fullWidth
                      startIcon={<CompareArrowsIcon />}
                      variant="outlined"
                      color={isCompared ? "success" : "primary"}
                      onClick={() => isCompared ? updateHosting("prices") : setIsCompared(true)}
                      sx={{ borderRadius: "10px", py: 1 }}
                    >
                      {isCompared ? "Valider les prix" : "Comparer prix OVH"}
                    </Button>
                    {isCompared && (
                       <Button fullWidth size="small" color="error" onClick={() => setIsCompared(false)}>Annuler comparaison</Button>
                    )}
                  </Stack>
                </Stack>
              )}
            </Card>
          </Grid>
        )}
      </Grid>

      {openConfirmClose && (
        <AlerteDialog
          open={openConfirmClose}
          handleClose={() => setOpenConfirmClose(false)}
          deleteProject={() => { setIsModified(false); setOpenConfirmClose(false); }}
          title="Quitter sans enregistrer ?"
          contentText="Attention, toute modification non enregistrée sera perdue."
        />
      )}
    </Box>
  );
};

export default HostingDetails;